import { X, MessageSquare, Trash2, Edit2, Check, Calendar, FileText } from 'lucide-react';
import { useState, useEffect } from 'react';

interface SavedChat {
  id: string;
  name: string;
  flow_name: string;
  timestamp: string;
  messages: Array<{
    role: 'bot' | 'user';
    content: string;
    timestamp: string;
  }>;
  session_id: string;
}

interface ChatHistoryPanelProps {
  onClose: () => void;
  onLoadChat: (chat: SavedChat) => void;
}

export default function ChatHistoryPanel({ onClose, onLoadChat }: ChatHistoryPanelProps) {
  const [savedChats, setSavedChats] = useState<SavedChat[]>([]);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');

  useEffect(() => {
    loadChats();
  }, []);

  const loadChats = () => {
    const saved = localStorage.getItem('pigna-flow-chat-history');
    if (saved) {
      try {
        const chats = JSON.parse(saved);
        setSavedChats(chats);
      } catch (e) {
        console.error('Errore caricamento chat:', e);
        setSavedChats([]);
      }
    }
  };

  const deleteChat = (id: string, name: string) => {
    if (confirm(`⚠️ Eliminare la conversazione "${name}"?`)) {
      const updated = savedChats.filter(c => c.id !== id);
      setSavedChats(updated);
      localStorage.setItem('pigna-flow-chat-history', JSON.stringify(updated));
      alert(`✅ Chat "${name}" eliminata!`);
    }
  };

  const startEditingName = (chat: SavedChat) => {
    setEditingChatId(chat.id);
    setEditingName(chat.name);
  };

  const saveEditedName = (chatId: string) => {
    if (!editingName.trim()) {
      alert('❌ Il nome non può essere vuoto');
      return;
    }
    const updated = savedChats.map(chat =>
      chat.id === chatId ? { ...chat, name: editingName.trim() } : chat
    );
    setSavedChats(updated);
    localStorage.setItem('pigna-flow-chat-history', JSON.stringify(updated));
    setEditingChatId(null);
    setEditingName('');
    alert(`✅ Chat rinominata in "${editingName.trim()}"!`);
  };

  const cancelEditingName = () => {
    setEditingChatId(null);
    setEditingName('');
  };

  // Removed - using exportChatAsText instead
  // const exportChat = (chat: SavedChat) => {
  //   const blob = new Blob([JSON.stringify(chat, null, 2)], { type: 'application/json' });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url;
  //   a.download = `${chat.name.replace(/\s+/g, '_')}_${chat.id}.json`;
  //   a.click();
  // };

  const exportChatAsText = (chat: SavedChat) => {
    let text = `=== ${chat.name} ===\n`;
    text += `Flow: ${chat.flow_name}\n`;
    text += `Data: ${new Date(chat.timestamp).toLocaleString('it-IT')}\n`;
    text += `\n--- CONVERSAZIONE ---\n\n`;
    
    chat.messages.forEach(msg => {
      const time = new Date(msg.timestamp).toLocaleTimeString('it-IT');
      const speaker = msg.role === 'bot' ? '🤖 BOT' : '👤 USER';
      text += `[${time}] ${speaker}:\n${msg.content}\n\n`;
    });

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${chat.name.replace(/\s+/g, '_')}_${chat.id}.txt`;
    a.click();
  };

  const sortedChats = [...savedChats].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    } else {
      return a.name.localeCompare(b.name);
    }
  });

  return (
    <div className="w-[600px] bg-white border-l-4 border-green-300 flex flex-col overflow-hidden shadow-2xl h-full">
      {/* Header */}
      <div className="p-6 border-b-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-green-500" />
            Cronologia Chat ({savedChats.length})
          </h2>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-lg transition"
            title="Chiudi pannello"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span>Ordina per:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'name')}
            className="px-2 py-1 border rounded"
          >
            <option value="date">📅 Per data</option>
            <option value="name">🔤 Per nome</option>
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {sortedChats.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            <p className="text-lg font-semibold mb-2">Nessuna chat salvata.</p>
            <p className="text-sm">Avvia il simulatore e salva le tue conversazioni!</p>
          </div>
        ) : (
          sortedChats.map(chat => (
            <div
              key={chat.id}
              className="bg-white rounded-xl shadow-lg border-2 border-green-200 hover:border-green-400 transition p-4"
            >
              <div className="flex items-center justify-between mb-2">
                {editingChatId === chat.id ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 px-2 py-1 border-2 border-green-300 rounded text-sm font-semibold"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEditedName(chat.id);
                      if (e.key === 'Escape') cancelEditingName();
                    }}
                  />
                ) : (
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{chat.name}</h3>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(chat.timestamp).toLocaleString('it-IT')}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <FileText size={12} />
                        {chat.flow_name}
                      </span>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 ml-4">
                  {editingChatId === chat.id ? (
                    <>
                      <button
                        onClick={() => saveEditedName(chat.id)}
                        className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded transition"
                        title="Salva nome"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={cancelEditingName}
                        className="p-1.5 bg-gray-400 hover:bg-gray-500 text-white rounded transition"
                        title="Annulla"
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          onLoadChat(chat);
                          onClose();
                        }}
                        className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded transition"
                        title="Carica conversazione"
                      >
                        📂 Carica
                      </button>
                      <button
                        onClick={() => startEditingName(chat)}
                        className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded transition"
                        title="Rinomina"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => exportChatAsText(chat)}
                        className="p-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded transition"
                        title="Esporta TXT"
                      >
                        📄
                      </button>
                      <button
                        onClick={() => deleteChat(chat.id, chat.name)}
                        className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition"
                        title="Elimina chat"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-700 bg-green-50 p-3 rounded border border-green-200">
                <div className="font-semibold mb-1">💬 {chat.messages.length} messaggi</div>
                <div className="text-xs text-gray-600 line-clamp-2">
                  {chat.messages[chat.messages.length - 1]?.content.substring(0, 100)}...
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

